import {
    Grid,
    List,
    ListItem,
    ListItemText,
    Theme,
    withStyles,
    WithStyles,
    Typography,
    Collapse,
    IconButton,
    CircularProgress
} from '@material-ui/core'
import CreditStatusTooltip from '../../Tooltips/CreditStatusTooltip'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import { FC, Fragment, useState, useEffect, useCallback } from 'react'
import { getFirstCharCapitalized } from '../../../libs/strings'
import { MuiClasses } from '../../../types'
import { formatStringIfDate } from '../../../utils/dates'
import PaymentPopup from '../../../containers/PayForPublish/PaymentPopup'
import { Apartment } from '../../../types/apartments'
import { Application } from '../../../types/applications'
import ApplicationsApi from '../../../services/applications'
import digivakuusEligible from '../../../images/afterShowing/digivakuusEligible.svg'
import DigivakuusFaqLink from '../../Digivakuus/DigivakuusFaqLink'

const styles = ({ breakpoints }: Theme) => ({
    listItems: {
        padding: '0',
        margin: '0',
        marginBottom: '13px',
        marginTop: '3px',
        color: '#0000008A',
    },
    listItemsTopPart: {
        padding: '0',
        margin: '0',
        color: '#0000008A',
    },
    listItemsTitle: {
        padding: '0',
        paddingTop: '7px',
        margin: '0',
        'text-transform': 'uppercase',
        fontSize: '0.7rem !important',
        '& p': {
            color: '#000000DE',
            fontFamily: 'CircularBold'
        },
    },
    spouseLifeSituation: {
        '& span': {
            fontSize: '1.2rem'
        }
    },
    applicantInfoSubheader: {
        marginTop: '16px',
        marginBottom: '16px',
    },
    // InfoBox styles (copied from AfterShowing last step)
    infoBox: {
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'flex-start',
        padding: 24,
        gap: 12,
        width: '100%',
        borderRadius: 4,
        boxSizing: 'border-box' as const,
        margin: '0 0 24px 0',
    },
    successBox: {
        background: '#FBF6EE'
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12
    },
    infoIcon: {
        width: 40,
        height: 40,
        borderRadius: 9999,
        flex: '0 0 auto'
    },
    infoTitle: {
        fontWeight: 700,
        fontSize: 18,
        lineHeight: '22px',
        color: '#000'
    },
    infoDesc: {
        fontWeight: 400,
        fontSize: 14,
        lineHeight: '20px',
        color: '#000'
    },
    faqButton: {
        marginTop: 8,
        marginLeft: -6,
        fontSize: 16,
        textTransform: 'none' as const,
        textDecoration: 'underline',
        fontWeight: 600,
        color: '#94004A',
        '&:hover': {
            textDecoration: 'underline'
        }
    }
})

export interface CustomListWithCreditInfoProps extends WithStyles {
    classes: MuiClasses
    TitleTypography?: JSX.Element
    data: any
    applicants: any
    informationOrder: string[]
    translationPath: string
    t: i18next.TFunction
    multiSelectPaths?: { [key: string]: string }
    hideListItemLogic?: (key: string, value: string) => boolean
    capitalizeFirstLetter?: boolean,
    apartment?: Apartment,
    application?: Application
}

interface ListItemProps {
    t: TFunction
    objectValue: string
    capitalizeFirstLetter?: boolean
}

interface MultiSelectTranslationProps extends ListItemProps {
    multiSelectPath: string
}

const getTranslatedMultiValue = ({
    t,
    multiSelectPath,
    objectValue,
    capitalizeFirstLetter
}: MultiSelectTranslationProps) => {
    const translatedValue = t(`${multiSelectPath}.${objectValue}`)
    return capitalizeFirstLetter
        ? getFirstCharCapitalized(translatedValue)
        : translatedValue
}

const getCapitalized = ({
    t,
    objectValue,
    capitalizeFirstLetter
}: ListItemProps) => {
    const translatedValue = t(`${formatStringIfDate(objectValue)}`)
    return capitalizeFirstLetter
        ? getFirstCharCapitalized(translatedValue)
        : translatedValue
}

const CustomListWithCreditInfo: FC<CustomListWithCreditInfoProps> = ({
    classes,
    t,
    TitleTypography,
    data,
    applicants,
    informationOrder,
    translationPath,
    multiSelectPaths,
    hideListItemLogic,
    capitalizeFirstLetter,
    apartment,
    application
}) => {

    const [showCreditInfoSection, setShowCreditInfoSection] = useState(false)
    const [showBudgetInfoSection, setShowBudgetInfoSection] = useState(false)
    const [creditApplicants, setCreditApplicants] = useState(
        Array.isArray(applicants) ? applicants : []
    )
    const [loadingCreditInfo, setLoadingCreditInfo] = useState(false)
    const [creditInfoError, setCreditInfoError] = useState<string | null>(null)
    const [budgetInfo, setBudgetInfo] = useState(null)
    const [loadingBudgetInfo, setLoadingBudgetInfo] = useState(false)

    useEffect(() => {
        setCreditApplicants(Array.isArray(applicants) ? applicants : [])
    }, [applicants])

    const applicationRights = Array.isArray(application?.rights) ? application.rights as string[] : []
    const apartmentRights = Array.isArray(apartment?.rights) ? apartment.rights as string[] : []

    const hasCreditProduct = applicationRights.includes('7')
    const hasUnlimitedCreditChecks = apartmentRights.includes('12')
    const hasCreditAccess = hasCreditProduct || hasUnlimitedCreditChecks
    const hasBudgetRights = applicationRights.includes('9')

    useEffect(() => {
        if (showBudgetInfoSection && hasBudgetRights) {
            setLoadingBudgetInfo(true)
            fetchBudgetInfo()
        }
    }, [showBudgetInfoSection, hasBudgetRights, application])

    const handleToggleCreditInfo = () => {
        if (!hasCreditAccess && !hasBudgetRights) {
            // Both unpaid: toggle and close the other
            setShowCreditInfoSection(prev => {
                if (!prev) setShowBudgetInfoSection(false)
                return !prev
            })
        } else {
            setShowCreditInfoSection(prev => !prev)
        }
    }

    const handleToggleBudgetInfo = () => {
        if (!hasCreditAccess && !hasBudgetRights) {
            // Both unpaid: toggle and close the other
            setShowBudgetInfoSection(prev => {
                if (!prev) setShowCreditInfoSection(false)
                return !prev
            })
        } else {
            setShowBudgetInfoSection(prev => !prev)
        }
    }

    const fetchCreditInfo = useCallback(async () => {
        if (!application) return

        try {
            setLoadingCreditInfo(true)
            setCreditInfoError(null)

            let fetchedApplicants

            const response = await ApplicationsApi.getCreditInfo(application.applicationId, apartment.apartmentId)

            if (Array.isArray(response?.applicants)) {
                fetchedApplicants = response.applicants
            } else if (Array.isArray(response?.application?.applicants)) {
                fetchedApplicants = response.application.applicants
            } else if (Array.isArray(response?.data?.applicants)) {
                fetchedApplicants = response.data.applicants
            } else if (Array.isArray(response)) {
                fetchedApplicants = response
            }

            if (Array.isArray(fetchedApplicants)) {
                setCreditApplicants(fetchedApplicants)
            }
        } catch (err) {
            console.error('Error fetching credit info', err)
            setCreditInfoError('Unable to load credit information.')
        } finally {
            setLoadingCreditInfo(false)
        }
    }, [application, apartment])

    const fetchBudgetInfo = async () => {
        try {
            const response = await ApplicationsApi.getFinancialInfo(application.code) // Replace with your actual endpoint
            const data = await response
            setBudgetInfo(data.cases)
        } catch (err) {
            console.error("Error fetching budget info", err)
        } finally {
            setLoadingBudgetInfo(false)
        }
    }

    useEffect(() => {
        if (!showCreditInfoSection || !hasCreditAccess || loadingCreditInfo) {
            return
        }

        const currentApplicants = Array.isArray(application?.applicants)
            ? application.applicants
            : Array.isArray(applicants)
                ? applicants
                : []

        const creditMap = new Map(
            (creditApplicants || []).map((applicant: any) => [applicant?.code, applicant?.creditInfo])
        )

        const hasAnyCreditData = (creditApplicants || []).some((applicant: any) => applicant?.creditInfo)

        const creditCodes = new Set((creditApplicants || []).map((a: any) => a?.code).filter(Boolean))

        const missingByCode = currentApplicants.some((applicant: any) => {
            const code = applicant?.code
            if (!code) return true
            return !creditCodes.has(code)
        })

        const hasMissingCredit = currentApplicants.some((applicant: any) => {
            const code = applicant?.code
            if (!code) return true
            const info = creditMap.get(code)
            return !info || Object.keys(info || {}).length === 0
        })

        const shouldFetch = !hasAnyCreditData || missingByCode || hasMissingCredit

        if (shouldFetch) {
            fetchCreditInfo()
        }
    }, [
        showCreditInfoSection,
        hasCreditAccess,
        creditApplicants,
        applicants,
        application,
        loadingCreditInfo,
        fetchCreditInfo
    ])

    const depositMonthlyAmountText = '-'

    const digivakuusApplicants = Array.isArray(application?.applicants)
        ? application.applicants
        : Array.isArray(applicants)
            ? applicants
            : []

    const isDigivakuusEligible = digivakuusApplicants.some(
        (a: any) =>
            a &&
            a.digivakuusEligibility === true
    )
    const renderDigivakuusInfoBox = () => (
        <div className={`${classes.infoBox} ${classes.successBox}`}>
            <div className={classes.titleRow}>
                <img src={digivakuusEligible} className={classes.infoIcon} alt='' />
                <Typography className={classes.infoTitle}>
                    {t('afterShowing.eligibilityHeaderTrue')}
                </Typography>
            </div>
            <Typography
                className={classes.infoDesc}
                dangerouslySetInnerHTML={{
                    __html: t([
                        'afterShowing.eligibilityDescForLandlord',
                        'afterShowing.eligibilityDescTrue'
                    ]) as string
                }}
            />
            <DigivakuusFaqLink
                t={t}
                variant='landlord'
                buttonClassName={classes.faqButton}
                depositMonthlyAmountText={depositMonthlyAmountText}
            />
        </div>
    )

    const renderSwitch = ({
        key,
        objectValue,
        multiSelectPath,
        isErrorOrNoAnswer
    }: any) => {
        switch (key) {
            case 'creditStatus':
            case 'spouseCreditStatus':
                return (
                    <Grid item xs={12} md={6} lg={6}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: 13 }}>
                            <ListItemText
                                primary={t(`application.creditStatus.${objectValue}`)}
                                className={classes.listItems}
                                style={{ flex: '0 0 auto', marginBottom: 0 }}
                                primaryTypographyProps={{
                                    component: 'span',
                                    style: { display: 'inline' }
                                }}
                            />
                            <CreditStatusTooltip />
                        </div>
                    </Grid>
                )
            case 'phoneNumber':
                return (
                    <Grid item xs={12} md={6} lg={6}>
                        <a
                            style={{
                                textDecoration: 'none',
                                fontFamily: 'CircularRegular'
                            }}
                            href={`tel:+${objectValue}`}
                        >
                            +{objectValue}
                        </a>
                    </Grid>
                )
            case 'pets':
                return (
                    <Grid item xs={12} md={6} lg={6}>
                        <ListItemText
                            primary={getCapitalized({
                                t,
                                objectValue,
                                capitalizeFirstLetter
                            })}
                            className={classes.listItems}
                        />
                    </Grid>
                )
            case 'petType': {
                const normalizedPetType = typeof objectValue === 'string' ? objectValue.trim().toLowerCase() : ''
                const petLabel = normalizedPetType
                    ? t(`newApplication.petsOptions.${normalizedPetType}`, {
                        defaultValue: objectValue
                    })
                    : objectValue
                return (
                    <Grid item xs={12} md={6} lg={6}>
                        <ListItemText
                            primary={petLabel}
                            className={classes.listItems}
                        />
                    </Grid>
                )
            }
            default:
                return (
                    <Grid item xs={12} md={6} lg={6}>
                        {multiSelectPath && !isErrorOrNoAnswer ? (
                            <>
                                <ListItemText
                                    primary={getTranslatedMultiValue({
                                        t,
                                        objectValue,
                                        capitalizeFirstLetter,
                                        multiSelectPath
                                    })}
                                    className={classes.listItems}
                                />
                            </>
                        ) : (
                            <>
                                <ListItemText
                                    primary={getCapitalized({
                                        t,
                                        objectValue,
                                        capitalizeFirstLetter
                                    })}
                                    className={classes.listItems}
                                />
                            </>
                        )}
                    </Grid>
                )
        }
    }

    return (
        <Fragment>
            {/* POISTA TÄMÄ AIKANAAN */}
            {/* <pre>{JSON.stringify(data, null, 2)}</pre> */}
            {/* <pre>{JSON.stringify(applicants, null, 2)}</pre> */}
            {TitleTypography}

            <Typography
                variant='h5'
                className={classes.applicantInfoSubheader}
            >{t('applicantCard.generalInformation')}</Typography>

            <List
                component={'div' as any}
                disablePadding
                className={classes.innerList}
            >
                {informationOrder.map((key: string, index: number) => {
                    const isErrorOrNoAnswer = hideListItemLogic
                        ? data[key] === 'error' || hideListItemLogic(key, data[key])
                        : data[key] === 'error'
                    let objectValue = isErrorOrNoAnswer ? undefined : data[key]
                    const multiSelectPath = multiSelectPaths && multiSelectPaths[key]
                    let newPath = ''
                    if (
                        (key === 'livelihood' || key === 'spouseLivelihood') &&
                        Array.isArray(data[key])
                    ) {
                        newPath = `newApplication.livelihood`
                        objectValue = data[key]
                            .map((value: string) =>
                                value === 'myAnswer'
                                    ? data['livelihoodCustom']
                                    : t(`newApplication.livelihoodOptions.${value}`)
                            )
                            .join(', ')
                    }
                    if (key === 'movingAbout') {
                        newPath = 'application.list.readyForMoveDate'
                    }
                    if (
                        key === 'lifeSituationCustom' ||
                        key === 'spouseLifeSituationCustom' ||
                        key === 'spouseLifeSituation'
                    ) {
                        newPath = `application.list.lifeSituation`
                    }

                    if (key === 'depositPaymentMethodCustom') {
                        newPath = 'application.list.depositPaymentMethod'
                    }

                    if (objectValue === 'nextMonth') {
                        return <></>
                    }

                    if (key === 'spouseCreditStatus') {
                        newPath = 'application.list.creditStatusByApplicant'
                    }
                    if (key === 'creditStatus') {
                        newPath = 'application.list.creditStatusByApplicant'
                    }

                    if (!objectValue) {
                        return <></>
                    }

                    return (
                        <>
                            {key === 'spouseLifeSituation' && (
                                <ListItemText
                                    primary={t('application.suposeTitle')}
                                    className={classes.spouseLifeSituation}
                                    style={{
                                        margin: '10px 0'
                                    }}
                                />
                            )}
                            <ListItem key={`${key}+${index}`} className={classes.listItemsTopPart}>
                                <Grid
                                    container
                                    item
                                    direction='row'
                                    justify='space-between'

                                >
                                    <Grid item xs={12} md={6} lg={6}>
                                        <ListItemText
                                            secondary={
                                                newPath !== ''
                                                    ? t(`${newPath}`)
                                                    : t(`${translationPath}${key}`)
                                            }
                                            className={classes.listItemsTitle}
                                        />
                                    </Grid>
                                    {renderSwitch({
                                        key,
                                        objectValue,
                                        multiSelectPath,
                                        isErrorOrNoAnswer
                                })}
                            </Grid>
                        </ListItem>
                        </>
                    )
                })}
            </List>
            {isDigivakuusEligible && renderDigivakuusInfoBox()}
            {creditApplicants && creditApplicants.length > 0 && (
                <>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        margin: '12px 0',
                        // border: '1px solid #C3C6D1',
                        borderRadius: '4px',
                        // padding: '12px 16px',
                        // marginBottom: '18px',
                        backgroundColor: '#E7ECEF' // optional subtle background
                    }} onClick={handleToggleCreditInfo}>
                        <Typography variant="h6" style={{ flexGrow: 1, marginLeft: '8px' }}>
                            {t('applicantCard.showCreditInfoSection')} {/* or your custom title */}
                        </Typography>
                        <IconButton>
                            {showCreditInfoSection ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </div>

                    <Collapse in={showCreditInfoSection} timeout="auto" unmountOnExit>
                        {!hasCreditAccess ? (
                            <PaymentPopup
                                paymentProviderWidth={
                                    window.innerWidth < 380 ? '80px' :
                                        window.innerWidth < 520 ? '100px' :
                                            '150px'
                                }
                                productTags={['showCreditInfo']}
                                headertext={t('paymentPopup.headerTexts.showCreditInfo')}
                                infotext={t('paymentPopup.suggestTexts.showCreditInfo')}
                                apartment={apartment}
                                application={application}
                            />
                        ) : loadingCreditInfo ? (
                            <div style={{ padding: '16px', textAlign: 'center' }}>
                                <CircularProgress />
                            </div>
                        ) : creditInfoError ? (
                            <div style={{ padding: '16px' }}>
                                <Typography variant='body1' color='error'>
                                    {creditInfoError}
                                </Typography>
                            </div>
                        ) : (
                            <List component={'div' as any} disablePadding className={classes.innerList}>
                                {creditApplicants.map((applicant, applicantIndex) => (
                                    <>
                                        <div key={applicantIndex}>
                                            {creditApplicants.length > 1 && (
                                                <hr
                                                    style={{
                                                        border: '1px solid #C3C6D1',
                                                        borderTop: 'none',
                                                        marginTop: '11px',
                                                        marginBottom: '24px'
                                                    }}
                                                />
                                            )}
                                            <Typography variant='h5' className={classes.applicantInfoSubheader}>
                                                {creditApplicants.length > 1
                                                    ? `${t('applicantCard.applicant')} ${applicantIndex + 1} ${t('applicantCard.personalInformation')}`
                                                    : `${t('applicantCard.personalInformation')}`}
                                            </Typography>
                                            <ListItem className={classes.listItems}>
                                                <Grid container item direction='row' justify='space-between' style={{ margin: '1px 0' }}>
                                                    <Grid item xs={12} md={6} lg={6}>
                                                        <ListItemText secondary={t('applicantCard.name')} className={classes.listItemsTitle} />
                                                    </Grid>
                                                    <Grid item xs={12} md={6} lg={6}>
                                                        <ListItemText
                                                            primary={applicant.name}
                                                            className={classes.listItems}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={6} lg={6}>
                                                        <ListItemText secondary={t('applicantCard.idNumber')} className={classes.listItemsTitle} />
                                                    </Grid>
                                                    <Grid item xs={12} md={6} lg={6}>
                                                        <ListItemText
                                                            primary={applicant.idNumber}
                                                            className={classes.listItems}
                                                        />
                                                    </Grid>
                                                    {applicant.phone &&
                                                        <>
                                                            <Grid item xs={12} md={6} lg={6}>
                                                                <ListItemText secondary={t('applicantCard.phone')} className={classes.listItemsTitle} />
                                                            </Grid>
                                                            <Grid item xs={12} md={6} lg={6}>
                                                                <ListItemText
                                                                    primary={`+${applicant.phone.replace(/^(\+?)/, '')}`}
                                                                    className={classes.listItems}
                                                                />
                                                            </Grid>
                                                        </>
                                                    }
                                                    {applicant.email &&
                                                        <>
                                                            <Grid item xs={12} md={6} lg={6}>
                                                                <ListItemText secondary={t('applicantCard.email')} className={classes.listItemsTitle} />
                                                            </Grid>
                                                            <Grid item xs={12} md={6} lg={6}>
                                                                <ListItemText
                                                                    primary={applicant.email}
                                                                    className={classes.listItems}
                                                                />
                                                            </Grid>
                                                        </>
                                                    }
                                                    {/* <Grid item xs={12} md={6} lg={6}>
                                            <ListItemText secondary={t('applicantCard.status')} className={classes.listItemsTitle} />
                                        </Grid>
                                        <Grid item xs={12} md={6} lg={6}>
                                            <ListItemText
                                                primary={applicant.status}
                                                className={classes.listItems}
                                            />
                                        </Grid> */}
                                                    {applicant.creditInfo && (
                                                        <>
                                                            <Grid item xs={12} md={6} lg={6}>
                                                                <ListItemText secondary={t('applicantCard.creditStatus')} className={classes.listItemsTitle} />
                                                            </Grid>
                                                            <Grid item xs={12} md={6} lg={6}>
                                                                <ListItemText
                                                                    primary={t(`applicantCard.${applicant.creditStatus}`)}
                                                                    className={classes.listItems}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12} md={6} lg={6}>
                                                                <ListItemText secondary={t('applicantCard.paymentDefaultCount')} className={classes.listItemsTitle} />
                                                            </Grid>
                                                            <Grid item xs={12} md={6} lg={6}>
                                                                <ListItemText
                                                                    primary={applicant.creditInfo?.paymentDefaultCount}
                                                                    className={classes.listItems}
                                                                />
                                                            </Grid>
                                                        </>
                                                    )}
                                                    {applicant.creditInfo && applicant.creditInfo.paymentDefaultCount > 0 && applicant.creditInfo.creditInformationEntryRow && (
                                                        <>
                                                            {(Array.isArray(applicant.creditInfo.creditInformationEntryRow)
                                                                ? applicant.creditInfo.creditInformationEntryRow
                                                                : [applicant.creditInfo.creditInformationEntryRow]).map((entry: any, index: any) => (
                                                                    <>
                                                                        <Grid item xs={12}>
                                                                            <Typography
                                                                                variant='h6'
                                                                                style={{ marginTop: '15px', fontFamily: 'CircularBold' }}
                                                                            >
                                                                                {applicant.creditInfo.creditInformationEntryRow.length > 1
                                                                                    ? `${t('applicantCard.creditDefaultInformation')} ${index + 1}`
                                                                                    : t('applicantCard.creditDefaultInformation')}
                                                                            </Typography>
                                                                        </Grid>
                                                                        <Grid container key={index} style={{ margin: '5px 0' }}>
                                                                            <Grid item xs={12} md={6} lg={6}>
                                                                                <ListItemText secondary={t('applicantCard.creditor')} className={classes.listItemsTitle} />
                                                                            </Grid>
                                                                            <Grid item xs={12} md={6} lg={6}>
                                                                                <ListItemText primary={entry.creditor} className={classes.listItems} />
                                                                            </Grid>
                                                                            {entry.amountOfPaymentDefault && entry.amountOfPaymentDefault > 0 && (
                                                                                <>
                                                                                    <Grid item xs={12} md={6} lg={6}>
                                                                                        <ListItemText secondary={t('applicantCard.amountOfPaymentDefault')} className={classes.listItemsTitle} />
                                                                                    </Grid>
                                                                                    <Grid item xs={12} md={6} lg={6}>
                                                                                        <ListItemText primary={entry.amountOfPaymentDefault} className={classes.listItems} />
                                                                                    </Grid>
                                                                                </>
                                                                            )}
                                                                            <Grid item xs={12} md={6} lg={6}>
                                                                                <ListItemText secondary={t('applicantCard.creditInformationEntryText')} className={classes.listItemsTitle} />
                                                                            </Grid>
                                                                            <Grid item xs={12} md={6} lg={6}>
                                                                                <ListItemText primary={entry.creditInformationEntryText} className={classes.listItems} />
                                                                            </Grid>
                                                                            <Grid item xs={12} md={6} lg={6}>
                                                                                <ListItemText secondary={t('applicantCard.creditInformationEntryDate')} className={classes.listItemsTitle} />
                                                                            </Grid>
                                                                            <Grid item xs={12} md={6} lg={6}>
                                                                                <ListItemText primary={entry.creditInformationEntryDate} className={classes.listItems} />
                                                                            </Grid>
                                                                            <Grid item xs={12} md={6} lg={6}>
                                                                                <ListItemText secondary={t('applicantCard.creditInformationEntryExpires')} className={classes.listItemsTitle} />
                                                                            </Grid>
                                                                            <Grid item xs={12} md={6} lg={6}>
                                                                                <ListItemText primary={entry.creditInformationEntryExpires} className={classes.listItems} />
                                                                            </Grid>
                                                                            <Grid item xs={12} md={6} lg={6}>
                                                                                <ListItemText secondary={t('applicantCard.sourceofinformation')} className={classes.listItemsTitle} />
                                                                            </Grid>
                                                                            <Grid item xs={12} md={6} lg={6}>
                                                                                <ListItemText primary={entry.sourceOfInformation} className={classes.listItems} />
                                                                            </Grid>
                                                                        </Grid>
                                                                    </>
                                                                ))}
                                                        </>
                                                    )}
                                                </Grid>
                                            </ListItem>
                                        </div >
                                    </>
                                ))}
                            </List>
                        )}
                    </Collapse>
                </>
            )}
            {application?.budgetInformation?.some(entry =>
                entry?.status === 'Finished'
            ) && (
                    <>
                        <hr style={{ border: '1px solid #C3C6D1', borderTop: 'none', marginTop: '-12px', marginBottom: '-12px' }} />

                        <div style={{ marginBottom: '26px', }} >
                            <div style={{
                                display: 'flex', alignItems: 'center', cursor: 'pointer', margin: '12px 0', borderRadius: '4px',
                                // padding: '12px 16px',
                                // marginBottom: '18px',
                                backgroundColor: '#E7ECEF'
                            }} onClick={handleToggleBudgetInfo}>
                                <Typography variant="h6" style={{ flexGrow: 1, marginLeft: '8px' }}>
                                    {t('applicantCard.showBudgetInfoSection')} {/* or your custom title */}
                                </Typography>
                                <IconButton>
                                    {showBudgetInfoSection ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                            </div>
                            <Collapse in={showBudgetInfoSection} timeout="auto" unmountOnExit>
                                {hasBudgetRights ? (
                                    loadingBudgetInfo ? (
                                        <div style={{ padding: '16px', textAlign: 'center' }}>
                                            <CircularProgress />
                                        </div>
                                    ) : (
                                        <>
                                            {budgetInfo && (Array.isArray(budgetInfo) ? budgetInfo : [budgetInfo]).map((entry, index) => {
                                                const months = entry?.data?.budgetOutput?.psd2?.insights?.months || []
                                                const firstThree = months.slice(0, 3)

                                                const itemSums = {}

                                                firstThree.forEach(month => {
                                                    Object.entries(month || {}).forEach(([key, val]) => {
                                                        if (key === 'month') return
                                                        const amount = typeof val === 'number' ? val : 0
                                                        if (!itemSums[key]) itemSums[key] = 0
                                                        itemSums[key] += amount
                                                    })
                                                })

                                                Object.keys(itemSums).forEach(key => {
                                                    itemSums[key] = Math.round(itemSums[key] * 100) / 100
                                                  })

                                                const incomeItems = Object.entries(itemSums).filter(([_, val]) => typeof val === 'number' && val > 0)
                                                const expenseItems = Object.entries(itemSums).filter(([_, val]) => typeof val === 'number' && val < 0)

                                                const round2 = (num: number) => Math.round(num * 100) / 100

                                                const totalIncome = round2(firstThree.reduce((sum, m) => sum + (m.totalIncome || 0), 0))
                                                const totalExpenses = round2(firstThree.reduce((sum, m) => sum + (m.totalExpenses || 0), 0))
                                                const netAvailable = round2(totalIncome + totalExpenses)

                                                return (
                                                    <Fragment key={index}>
                                                        <Grid container style={{ margin: '12px 0' }}>
                                                            {entry?.data?.consumerName && (
                                                                <>
                                                                    <Grid item xs={12} md={6} lg={6}>
                                                                        <ListItemText
                                                                            secondary={t('applicantCard.budgetInfoSection.applicantName')}
                                                                            className={classes.listItemsTitle}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={12} md={6} lg={6}>
                                                                        <ListItemText
                                                                            primary={entry.data.consumerName}
                                                                            className={classes.listItems}
                                                                        />
                                                                    </Grid>
                                                                </>
                                                            )}

                                                            {incomeItems.length > 0 && (
                                                                <>
                                                                    <Grid item xs={12}>
                                                                        <Typography
                                                                            variant="h6"
                                                                            className={classes.listItems}
                                                                            style={{ marginTop: '12px', marginBottom: '6px', fontFamily: 'CircularBold' }}
                                                                        >
                                                                            {t('applicantCard.budgetInfoSection.incomeSubheader')}
                                                                        </Typography>
                                                                    </Grid>
                                                                    {incomeItems.map(([key, val]) => (
                                                                        <Fragment key={key}>
                                                                            <Grid item xs={12} md={6} lg={6}>
                                                                                <ListItemText
                                                                                    secondary={t(`applicantCard.budgetInfoSection.categories.${key}`)}
                                                                                    className={classes.listItemsTitle}
                                                                                />
                                                                            </Grid>
                                                                            <Grid item xs={12} md={6} lg={6}>
                                                                                <ListItemText primary={val} className={classes.listItems} />
                                                                            </Grid>
                                                                        </Fragment>
                                                                    ))}
                                                                </>
                                                            )}

                                                            {expenseItems.length > 0 && (
                                                                <>
                                                                    <Grid item xs={12}>
                                                                        <Typography
                                                                            variant="h6"
                                                                            className={classes.listItems}
                                                                            style={{ marginTop: '12px', marginBottom: '6px', fontFamily: 'CircularBold' }}
                                                                        >
                                                                            {t('applicantCard.budgetInfoSection.expensesSubheader')}
                                                                        </Typography>
                                                                    </Grid>
                                                                    {expenseItems.map(([key, val]) => (
                                                                        <Fragment key={key}>
                                                                            <Grid item xs={12} md={6} lg={6}>
                                                                                <ListItemText
                                                                                    secondary={t(`applicantCard.budgetInfoSection.categories.${key}`)}
                                                                                    className={classes.listItemsTitle}
                                                                                />
                                                                            </Grid>
                                                                            <Grid item xs={12} md={6} lg={6}>
                                                                                <ListItemText primary={val} className={classes.listItems} />
                                                                            </Grid>
                                                                        </Fragment>
                                                                    ))}
                                                                </>
                                                            )}

                                                            <Grid item xs={12}>
                                                                <Typography
                                                                    variant='h6'
                                                                    className={classes.listItems}
                                                                    style={{ marginTop: '12px', marginBottom: '6px', fontFamily: 'CircularBold' }}
                                                                >
                                                                    {t('applicantCard.budgetInfoSection.summarySubheader')}
                                                                </Typography>
                                                            </Grid>

                                                            {/* <Grid item xs={12} md={6} lg={6}>
                                                                <ListItemText
                                                                    secondary={t('applicantCard.budgetInfoSection.totalIncome')}
                                                                    className={classes.listItemsTitle}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12} md={6} lg={6}>
                                                                <ListItemText primary={totalIncome} className={classes.listItems} />
                                                            </Grid>

                                                            <Grid item xs={12} md={6} lg={6}>
                                                                <ListItemText
                                                                    secondary={t('applicantCard.budgetInfoSection.totalExpenses')}
                                                                    className={classes.listItemsTitle}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12} md={6} lg={6}>
                                                                <ListItemText primary={totalExpenses} className={classes.listItems} />
                                                            </Grid> */}

                                                            <Grid item xs={12} md={6} lg={6}>
                                                                <ListItemText
                                                                    secondary={t('applicantCard.budgetInfoSection.totalNetFromThreeMonths')}
                                                                    className={classes.listItemsTitle}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12} md={6} lg={6}>
                                                                <ListItemText primary={netAvailable} className={classes.listItems} />
                                                            </Grid>

                                                            {typeof entry?.data?.budgetOutput?.psd2?.insights?.currentBalance === 'number' && entry?.data?.updatedOn && (
                                                                <>
                                                                    <Grid item xs={12} md={6} lg={6}>
                                                                        <ListItemText
                                                                            secondary={`${t('applicantCard.budgetInfoSection.balancePer')} ${new Date(entry.data.updatedOn).toLocaleDateString('fi-FI')}`}
                                                                            className={classes.listItemsTitle}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={12} md={6} lg={6}>
                                                                        <ListItemText
                                                                            primary={entry.data.budgetOutput.psd2.insights.currentBalance}
                                                                            className={classes.listItems}
                                                                        />
                                                                    </Grid>
                                                                </>
                                                            )}
                                                        </Grid>
                                                    </Fragment>
                                                )
                                            })}
                                        </>
                                    )
                                ) : (
                                    <PaymentPopup
                                        paymentProviderWidth={
                                            window.innerWidth < 380 ? '80px' :
                                                window.innerWidth < 520 ? '100px' : '150px'
                                        }
                                        productTags={['showBudgetInfo']}
                                        headertext={t('paymentPopup.headerTexts.showBudgetInfo')}
                                        infotext={t('paymentPopup.suggestTexts.showBudgetInfo')}
                                        apartment={apartment}
                                        application={application}
                                    />
                                )}
                            </Collapse>

                        </div>
                    </>
                )}
        </Fragment >
    )
}

export default withStyles(styles)(CustomListWithCreditInfo)
